import { useFinixJs } from "hooks/useFinixJs";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

interface IFinixPaymentFormProps {
  applicationId: string;
  environment: "sandbox" | "live";
  onCardComplete?: (complete: boolean) => void;
  onTokenSuccess?: (token: string) => void;
  onTokenError?: (error: string) => void;
}

export interface IFinixPaymentFormHandle {
  submit: () => void;
  /** Tokenize without calling onTokenSuccess (CLE-2798: read the card type before Pay). */
  tokenize: () => Promise<string>;
}

const StyledCardContainer = styled.div`
  .field-wrapper {
    margin-bottom: 12px;
  }

  iframe {
    border: none !important;
  }
`;

export const FinixPaymentForm = forwardRef<
  IFinixPaymentFormHandle,
  IFinixPaymentFormProps
>(
  (
    {
      applicationId,
      environment,
      onCardComplete,
      onTokenSuccess,
      onTokenError,
    },
    ref
  ) => {
    const {
      finix,
      isLoading: isFinixLoading,
      error: finixError,
    } = useFinixJs();
    const formRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Use a stable unique ID to avoid collisions if multiple forms render
    const formIdRef = useRef(
      `finix-card-form-${Math.random().toString(36).slice(2)}`
    );

    // Store latest callbacks in refs so initForm doesn't depend on them
    const onCardCompleteRef = useRef(onCardComplete);
    onCardCompleteRef.current = onCardComplete;
    const onTokenErrorRef = useRef(onTokenError);
    onTokenErrorRef.current = onTokenError;

    const initForm = useCallback(() => {
      if (!finix || !containerRef.current || isInitialized) return;

      try {
        const form = finix.CardTokenForm(formIdRef.current, {
          applicationId,
          environment,
          showAddress: true,
          hideFields: [
            "address_line1",
            "address_line2",
            "address_city",
            "address_region",
            "address_state",
            "address_country",
          ],
          requiredFields: ["address_postal_code"],
          labels: {
            address_postal_code: "Billing ZIP",
          },
          placeholders: {
            address_postal_code: "ZIP",
          },
          onUpdate: (state: any) => {
            if (onCardCompleteRef.current) {
              const fieldValid = (field: any) =>
                field?.isDirty && field?.errorMessages?.length === 0;
              const isComplete =
                fieldValid(state?.name) &&
                fieldValid(state?.number) &&
                fieldValid(state?.expiration_date) &&
                fieldValid(state?.security_code) &&
                fieldValid(state?.["address.postal_code"]);
              onCardCompleteRef.current(!!isComplete);
            }
          },
          onLoad: () => {
            setIsInitialized(true);
          },
          styles: {
            default: {
              color: "#30313d",
              fontSize: "16px",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              "::placeholder": {
                color: "#757680",
              },
            },
            invalid: {
              color: "#df1b41",
            },
          },
        });

        formRef.current = form;
      } catch (err: any) {
        if (onTokenErrorRef.current) {
          onTokenErrorRef.current(
            err.message || "Failed to initialize card form"
          );
        }
      }
    }, [finix, applicationId, environment, isInitialized]);

    useEffect(() => {
      initForm();
    }, [initForm]);

    // Store latest callbacks in refs for useImperativeHandle
    const onTokenSuccessRef = useRef(onTokenSuccess);
    onTokenSuccessRef.current = onTokenSuccess;

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (!formRef.current) {
            if (onTokenErrorRef.current) {
              onTokenErrorRef.current("Card form not initialized");
            }
            return;
          }

          formRef.current.submit(
            environment,
            applicationId,
            (err: any, res: any) => {
              if (err) {
                if (onTokenErrorRef.current) {
                  onTokenErrorRef.current(
                    err.message || "Card tokenization failed. Please try again."
                  );
                }
                return;
              }

              const tokenData = res?.data?.id;
              if (tokenData && onTokenSuccessRef.current) {
                onTokenSuccessRef.current(tokenData);
              } else if (onTokenErrorRef.current) {
                onTokenErrorRef.current("No token received from card form");
              }
            }
          );
        },
        // CLE-2798: a token for the checkout's background card read. It does not go through
        // onTokenSuccess, so it can never start a payment.
        tokenize: () =>
          new Promise<string>((resolve, reject) => {
            if (!formRef.current) {
              reject(new Error("Card form not initialized"));
              return;
            }
            formRef.current.submit(
              environment,
              applicationId,
              (err: any, res: any) => {
                const token = res?.data?.id;
                if (err || !token) {
                  reject(
                    new Error(
                      err?.message || "No token received from card form"
                    )
                  );
                  return;
                }
                resolve(token);
              }
            );
          }),
      }),
      [environment, applicationId]
    );

    if (finixError) {
      return (
        <p className="text-sm text-red-500">
          Failed to load payment form. Please refresh the page.
        </p>
      );
    }

    if (isFinixLoading) {
      return (
        <div className="flex justify-content-center p-3">
          <i className="pi pi-spinner pi-spin" />
        </div>
      );
    }

    return (
      <StyledCardContainer>
        <div id={formIdRef.current} ref={containerRef} />
      </StyledCardContainer>
    );
  }
);

FinixPaymentForm.displayName = "FinixPaymentForm";
